import React, { createContext, useCallback, useContext, useState } from 'react';
import { notificacionesService } from '@/services/api/notificaciones.service';
import type { Notificacion } from '@/modelos/notificaciones/notificacion.types';

interface NotificacionesContextType {
  notificaciones: Notificacion[];
  sinLeerCount: number;
  esCargando: boolean;
  cargarNotificaciones: () => Promise<void>;
  marcarLeida: (id: string) => Promise<void>;
  marcarTodasLeidas: () => Promise<void>;
}

const NotificacionesContext = createContext<NotificacionesContextType | null>(null);

export function NotificacionesProvider({ children }: { children: React.ReactNode }) {
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [sinLeerCount, setSinLeerCount] = useState(0);
  const [esCargando, setEsCargando] = useState(false);

  const cargarNotificaciones = useCallback(async () => {
    setEsCargando(true);
    try {
      const res = await notificacionesService.getAll(1, 20);
      if (res.ok && Array.isArray(res.data)) {
        setNotificaciones(res.data as unknown as Notificacion[]);
      }
      const countRes = await notificacionesService.getSinLeerCount();
      if (countRes.ok) setSinLeerCount(countRes.data?.count ?? 0);
    } catch {
      // silencioso
    } finally {
      setEsCargando(false);
    }
  }, []);

  const marcarLeida = useCallback(async (id: string) => {
    await notificacionesService.marcarLeida(id);
    setNotificaciones(prev =>
      prev.map(n => n.id === id ? { ...n, fueLeida: true } : n)
    );
    setSinLeerCount(prev => Math.max(0, prev - 1));
  }, []);

  const marcarTodasLeidas = useCallback(async () => {
    await notificacionesService.marcarTodasLeidas();
    setNotificaciones(prev => prev.map(n => ({ ...n, fueLeida: true })));
    setSinLeerCount(0);
  }, []);

  return (
    <NotificacionesContext.Provider value={{
      notificaciones,
      sinLeerCount,
      esCargando,
      cargarNotificaciones,
      marcarLeida,
      marcarTodasLeidas,
    }}>
      {children}
    </NotificacionesContext.Provider>
  );
}

export function useNotificaciones() {
  const ctx = useContext(NotificacionesContext);
  if (!ctx) throw new Error('useNotificaciones debe usarse dentro de NotificacionesProvider');
  return ctx;
}
